import React, { useRef, useLayoutEffect, useEffect, isValidElement, cloneElement } from 'react';
import { debounce } from 'lodash';
import { useCacheStore } from '@src/shared/store/cache';

export interface RecoveryChildrenProps<T = any> {
  cachedValues?: T;
  onUpdate: (value: T) => void;
  onDispose: () => void;
}

export interface RecoveryProps<
  T = any,
  TC extends React.ReactElement<RecoveryChildrenProps<T>> = React.ReactElement<
    RecoveryChildrenProps<T>
  >,
> {
  cacheKey: string;
  children: TC;
  onUpdateDebounceMs?: number;
  cleanupOnUnmount?: boolean;
  cleanupOnKeyChange?: boolean;
}

function Recovery<T = any>({
  cacheKey,
  children,
  onUpdateDebounceMs,
  cleanupOnUnmount = false,
  cleanupOnKeyChange = false,
}: RecoveryProps<T>) {
  const { get: getCache, set: setCache, remove: removeCache } = useCacheStore();
  const childrenPropsOnUpdateRef = useRef(children.props.onUpdate);
  const childrenPropsOnDisposeRef = useRef(children.props.onDispose);
  const cacheKeyRef = useRef(cacheKey);

  const onUpdate = React.useMemo(() => {
    const fn = (v: T) => {
      if (cacheKey) {
        setCache(cacheKey, v);
      }

      return childrenPropsOnUpdateRef.current?.(v);
    };

    if (!onUpdateDebounceMs) return fn;

    return debounce(fn, onUpdateDebounceMs);
  }, [cacheKey, setCache, onUpdateDebounceMs]);

  const onDispose = React.useCallback(() => {
    removeCache(cacheKey);

    return childrenPropsOnDisposeRef.current?.();
  }, [cacheKey, removeCache]);

  useLayoutEffect(() => {
    childrenPropsOnUpdateRef.current = children.props.onUpdate;
  }, [children.props.onUpdate]);

  useLayoutEffect(() => {
    childrenPropsOnDisposeRef.current = children.props.onDispose;
  }, [children.props.onDispose]);

  useEffect(() => {
    return () => {
      if (cleanupOnUnmount) removeCache(cacheKey);
    };
  }, [cacheKey, cleanupOnUnmount, removeCache]);

  useEffect(() => {
    if (cleanupOnKeyChange && cacheKeyRef.current !== cacheKey) {
      removeCache(cacheKeyRef.current);
    }
    cacheKeyRef.current = cacheKey;
  }, [cacheKey, cleanupOnKeyChange, removeCache]);

  if (!isValidElement<RecoveryChildrenProps<T>>(children)) return null;

  return cloneElement(children, {
    ...children.props,
    cachedValues: getCache(cacheKey) as T,
    onUpdate,
    onDispose,
  });
}

export default React.memo(Recovery);
